import {
  AnnotationIcon,
  ChatAltIcon,
  DocumentTextIcon,
  LightBulbIcon,
} from "@heroicons/react/outline";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import {
  Calendar,
  Target05,
  Trophy01,
  Upload04,
} from "@untitled-ui/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  ActionLabel,
  CoachingDashboardItemNode,
  CoachingDimensionNode,
  CoachingEvidenceNode,
  CoachingInsightNode,
} from "types/graphql-schema";

import AppLink from "@components/link/link";

import { DimensionIcon, StatusBadge } from "./coaching-components";
import { priorityBadge } from "./coaching-constants";
import type { Priority } from "./coaching-constants";

const SOURCE_TYPES = [
  {
    match: "/meeting",
    icon: Calendar,
    label: "View meeting",
  },
  {
    match: "/goal",
    icon: Target05,
    label: "View goal",
  },
  {
    match: "/recognition",
    icon: Trophy01,
    label: "View recognition",
  },
  {
    match: "/checkin",
    icon: AnnotationIcon,
    label: "View check-in",
  },
  {
    match: "/work-summary",
    icon: DocumentTextIcon,
    label: "View work summary",
  },
  {
    match: "/work_summary",
    icon: DocumentTextIcon,
    label: "View work summary",
  },
  {
    match: "/review",
    icon: Upload04,
    label: "View review",
  },
  {
    match: "/feedback",
    icon: Upload04,
    label: "View feedback",
  },
  {
    match: "/assessment",
    icon: AnnotationIcon,
    label: "View assessment",
  },
  {
    match: "/note",
    icon: DocumentTextIcon,
    label: "View note",
  },
  {
    match: "/survey",
    icon: AnnotationIcon,
    label: "View survey",
  },
];

const getSourceType = (ev: CoachingEvidenceNode) => {
  const url = ev.sourceUrl ?? "";
  const text = (ev.text ?? "").toLowerCase();

  // Match by URL first
  for (const type of SOURCE_TYPES) {
    if (url.includes(type.match)) return type;
  }

  // Fall back to text content matching
  if (
    text.includes("meeting") ||
    text.includes("1-on-1") ||
    text.includes("1:1")
  )
    return { icon: Calendar, label: "View meeting" };
  if (
    text.includes("goal") ||
    text.includes("objective") ||
    text.includes("okr")
  )
    return { icon: Target05, label: "View goal" };
  if (
    text.includes("recognition") ||
    text.includes("kudos") ||
    text.includes("shout")
  )
    return { icon: Trophy01, label: "View recognition" };
  if (text.includes("check-in") || text.includes("checkin"))
    return { icon: AnnotationIcon, label: "View check-in" };
  if (text.includes("feedback") || text.includes("review"))
    return { icon: Upload04, label: "View feedback" };
  if (
    text.includes("summary") ||
    text.includes("update") ||
    text.includes("report")
  )
    return { icon: DocumentTextIcon, label: "View summary" };
  if (
    text.includes("action") ||
    text.includes("suggest") ||
    text.includes("recommend")
  )
    return { icon: LightBulbIcon, label: "View suggestion" };
  if (
    text.includes("schedule") ||
    text.includes("agenda") ||
    text.includes("calendar")
  )
    return { icon: Calendar, label: "View event" };

  return { icon: ChatAltIcon, label: "View source" };
};

const getEvidenceIcon = (ev: CoachingEvidenceNode) => getSourceType(ev).icon;

const getSourceLabel = (ev: CoachingEvidenceNode) => getSourceType(ev).label;

const ACTION_LABEL_DISPLAY: Record<ActionLabel, string> = {
  [ActionLabel.KeepDoing]: "Keep Doing",
  [ActionLabel.ManagerAction]: "Manager Action",
  [ActionLabel.OpenNextMeetingWith]: "Open Next Meeting With",
  [ActionLabel.SuggestedDiscussionTopic]: "Suggested Discussion Topic",
};

export type ExpandOverride = { expanded: boolean; version: number } | null;

const InsightCard = ({
  insight,
  expandOverride,
}: {
  insight: CoachingInsightNode;
  expandOverride: ExpandOverride;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (expandOverride) {
      setIsOpen(expandOverride.expanded);
    }
  }, [expandOverride?.version]);
  const badge = priorityBadge[(insight.priority ?? "low") as Priority];

  const filteredEvidence = (insight.evidence ?? []).filter(
    (ev): ev is CoachingEvidenceNode => ev != null,
  );

  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 overflow-hidden flex flex-col mb-1 p-2">
      <button
        type="button"
        className="px-3 py-2 flex items-center justify-between gap-3 cursor-pointer w-full text-left rounded-md hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h4 className="tf-header-base">{insight.title}</h4>
        <div className="flex items-center gap-2.5 shrink-0">
          {badge && <StatusBadge status={badge.status} label={badge.label} />}
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-3 flex flex-col gap-5">
              {/* Context section */}
              <div className="flex flex-col gap-[6px]">
                <h5 className="tf-header-sm">Context</h5>
                <p className="tf-body-sm leading-[1.4rem]">{insight.body}</p>
              </div>

              {/* Action section */}
              {insight.action && (
                <div className="flex flex-col gap-[6px]">
                  <h5 className="tf-header-sm">
                    {insight.action.label
                      ? (ACTION_LABEL_DISPLAY[
                          insight.action.label as ActionLabel
                        ] ?? insight.action.label)
                      : "Action"}
                  </h5>
                  <p className="tf-body-sm leading-[1.4rem]">
                    {insight.action.text}
                  </p>
                </div>
              )}

              {/* Evidence section */}
              {filteredEvidence.length > 0 && (
                <div className="flex flex-col gap-[6px]">
                  <h5 className="tf-header-sm">Evidence</h5>
                  <div className="flex flex-col gap-3">
                    {filteredEvidence.map((ev, i) => {
                      const Icon = getEvidenceIcon(ev);
                      return (
                        <div key={i} className="flex items-start gap-2">
                          <Icon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                          <p className="tf-body-sm leading-[1.4rem]">
                            {ev.text}
                            {ev.sourceUrl && (
                              <>
                                {" "}
                                <AppLink
                                  to={ev.sourceUrl}
                                  className="text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  {getSourceLabel(ev)}
                                </AppLink>
                              </>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const DimensionSection = ({
  dimension,
  dashboardCards,
  expandOverride,
}: {
  dimension: CoachingDimensionNode;
  dashboardCards: CoachingDashboardItemNode[];
  expandOverride: ExpandOverride;
}) => {
  const dashboardDetail = dashboardCards.find(
    (card) => card.dimension === dimension.name,
  )?.detail;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-0.5 mb-3">
        <div className="flex items-center gap-2.5">
          <DimensionIcon
            name={dimension.name ?? ""}
            className="h-4 w-4 text-gray-500"
          />
          <h3 className="tf-header-lg-dark">{dimension.name}</h3>
        </div>
        {dashboardDetail && (
          <p className="pl-[26px] text-sm text-gray-500">{dashboardDetail}</p>
        )}
      </div>
      <div className="flex flex-col gap-2 pl-6 mb-12 pt-1">
        {(dimension.insights ?? [])
          .filter((insight): insight is CoachingInsightNode => insight != null)
          .sort((a, b) => {
            const order: Record<string, number> = {
              high: 0,
              medium: 1,
              low: 2,
              positive: 3,
            };
            return (
              (order[a.priority ?? "positive"] ?? 4) -
              (order[b.priority ?? "positive"] ?? 4)
            );
          })
          .map((insight, i) => (
            <InsightCard
              key={insight.title ?? i}
              insight={insight}
              expandOverride={expandOverride}
            />
          ))}
      </div>
    </div>
  );
};
